import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextArea } from '@/components/forms/form-text-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createCoachPool } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { type CoachPoolFilters } from '@airreading/common/data';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { coachPoolFilterLabels } from './coach-pool-filter-labels';
import { pluralize } from '@/lib/string-utils';

const formSchema = z.object({
  title: z.string().trim().min(1, 'Pool name is required'),
  description: z.string().optional(),
});

interface CreateCoachPoolProps {
  filters: CoachPoolFilters;
  matchingTeachersCount: number;
}

export const CreateCoachPool = ({ filters, matchingTeachersCount }: CreateCoachPoolProps) => {
  const { mutateAsync } = useMutation({ mutationFn: createCoachPool });
  const { toast } = useToast();
  const labels = coachPoolFilterLabels(filters);
  const teacherLabel = pluralize(matchingTeachersCount, 'teacher', 'teachers');

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    try {
      await mutateAsync({
        ...filters,
        title: formData.title,
        description: formData.description || undefined,
      });
      toast({ title: 'Teacher pool created successfully' });
      closeModal();
    } catch (error) {
      toast({
        title: airServerErrorFrom(error)?.userFacingMessage ?? 'Teacher pool creation failed',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Create reserved teacher pool"
      formSchema={formSchema}
      defaultValues={{ title: '', description: '' }}
      onSubmit={onSubmit}
      confirmBtnLabel="Create pool"
      dataTestId="teacher-pool-modal-create"
      dialogContentProps={{ className: 'lg:max-w-xl' }}
      trigger={
        <Button disabled={labels.length === 0} data-testid="teachers-availabilities-button-create-pool">
          Create pool
        </Button>
      }
    >
      {({ form }) => (
        <div className="flex flex-col gap-4 py-4">
          <FormInputField
            name="title"
            label="Pool name"
            form={form}
            inputProps={{
              className: 'w-full',
              required: true,
              placeholder: 'e.g. EBR October launch',
              'data-testid': 'teacher-pool-modal-create-input-title',
            }}
          />
          <FormTextArea
            name="description"
            label="Description (optional)"
            form={form}
            textAreaProps={{
              placeholder: 'What are these teachers being reserved for?',
              'data-testid': 'teacher-pool-modal-create-input-description',
            }}
          />
          <section
            aria-labelledby="create-teacher-pool-matching-teachers"
            className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3"
            data-testid="teacher-pool-modal-create-filters"
          >
            <h3 id="create-teacher-pool-matching-teachers" className="text-sm font-bold">
              {matchingTeachersCount} matching {teacherLabel}
            </h3>
            {labels.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {labels.map((label) => (
                  <li key={label}>
                    <Badge variant="outline" className="font-normal">
                      {label}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No filters applied.</p>
            )}
          </section>
          <p className="text-sm text-muted-foreground">
            All {matchingTeachersCount} matching {teacherLabel} will be included, across all pages. Checked rows do not
            limit the pool.
          </p>
        </div>
      )}
    </FormModal>
  );
};
